import { useQuery } from '@tanstack/react-query'

import { fetchActiveOrders } from '../api/activeOrdersApi'
import { sortActiveOrdersByDate } from '../utils/activeOrders'

import type { EnvironmentWithChina } from '../App'

interface UseActiveOrdersOptions {
  accountNumber: string
  env: EnvironmentWithChina
  authToken?: string
  /** Set false to hold the request back — e.g. while `CH-active-cards` is off. */
  enabled?: boolean
}

/**
 * Shared `active_orders` query. The overview section and the order-details
 * cards both call this; the common query key means React Query serves both
 * from a single fetch.
 *
 * Sorting lives here rather than in either view so the two can never disagree.
 * `select` is a stable module-level reference, so React Query memoises it and
 * the array is not re-sorted on every render.
 */
export function useActiveOrders({
  accountNumber,
  env,
  authToken,
  enabled = true,
}: UseActiveOrdersOptions) {
  return useQuery({
    queryKey: ['activeOrders', accountNumber, env],
    queryFn: () => fetchActiveOrders(accountNumber, env, authToken),
    select: sortActiveOrdersByDate,
    enabled: enabled && Boolean(accountNumber),
    staleTime: 60 * 1000,
  })
}
